import { type ProfileDto } from "@crowdcoursing/api/openapi";
import { useQueryClient } from "@tanstack/react-query";
import { lazy, Suspense } from "react";
import { createBrowserRouter, createMemoryRouter, Navigate, Outlet, type RouteObject } from "react-router-dom";

import { AdminPageAsync } from "~ui/pages/AdminPage/AdminPageAsync";
import { AIChatPageAsync } from "~ui/pages/AIChatPage/AIChatPageAsync";
import { LegacyAppNavigationLayoutAsync } from "~ui/pages/AppNavigationLayout/AppNavigationLayoutAsync";
import { CandidateSearchAsync } from "~ui/pages/CandidateSearch/CandidateSearchAsync";
import { CreateProfileAsync } from "~ui/pages/CreateProfile/CreateProfileAsync";
import { EventDashboardPage } from "~ui/pages/EventDashboardPage/EventDashboardPage";
import { HomeAsync } from "~ui/pages/Home/HomeAsync";
import { LoginAsync } from "~ui/pages/Login/LoginAsync";
import { LoginCallbackAsync } from "~ui/pages/LoginCallback/LoginCallbackAsync";
import { LoginVerificationAsync } from "~ui/pages/LoginVerification/LoginVerificationAsync";
import { Logout } from "~ui/pages/Logout/Logout";
import { MyEventsPageAsync } from "~ui/pages/MyEventsPage/MyEventsPageAsync";
import { MyRoomsPageAsync } from "~ui/pages/MyRoomsPage/MyRoomsPageAsync";
import { ArchivedAsync } from "~ui/pages/Notifications/ArchivedAsync";
import { InboxAsync } from "~ui/pages/Notifications/InboxAsync";
import { OrganizationAsync } from "~ui/pages/Organization/OrganizationAsync";
import { ResourceLibraryPageAsync } from "~ui/pages/ResourceLibraryPage/ResourceLibraryPageAsync";
import { ResumeBuilderAsync } from "~ui/pages/ResumeBuilder/ResumeBuilderAsync";
import { RoomDashboardPage } from "~ui/pages/RoomDashboardPage/RoomDashboardPage";
import { PipelineAsync } from "~ui/pages/SalesPortal/PipelineAsync";
import { ProspectDetailsAsync } from "~ui/pages/SalesPortal/ProspectDetailsAsync";
import { ProspectsAsync } from "~ui/pages/SalesPortal/ProspectsAsync";
import { SalesHomeAsync } from "~ui/pages/SalesPortal/SalesHomeAsync";
import { DataAnalyticsPage } from "~ui/pages/SiteAdminPage/DataAnalyticsPage";
import { EntityManagementPage } from "~ui/pages/SiteAdminPage/EntityManagementPage";
import { TaskDashboardPage } from "~ui/pages/TaskDashboardPage/TaskDashboardPage";
import { UserProfileAsync } from "~ui/pages/UserProfile/UserProfileAsync";
import { isTokenExpired } from "~utils/url";

import { AppLayoutAsync } from "../../AppLayoutAsync";
import LoadingIndicatorFullPage from "../../cc-ui/components/LoadingIndicator/LoadingIndicatorFullPage";
import { CheckIn as EventCheckIn } from "../../components/Events/CheckIn";
import { Cancel as PaymentCancel, Success as PaymentSuccess } from "../../components/Payments";
import { CheckIn as PodCheckIn } from "../../components/Pods/CheckIn";
import { ProfileRefresh } from "../../components/Profiles/PaymentAccountForm";
import { RedirectToLogin } from "../../components/RedirectToLogin";
import { useSelector } from "../../hooks";
import { CheckInPage } from "../../ui/pages/CheckIn/CheckInPage";
import { QRCodePage } from "../../ui/pages/CheckIn/QRCodePage";
import { MyOrganizationsPage } from "../../ui/pages/MyOrganizationsPage/MyOrganizationsPage";
import { AdminOrganizationsPage } from "../../ui/pages/OrganizationsPage/AdminOrganizationsPage";
import { RawRoute, Route } from "./Route";

// Import the unified search page component using lazy loading
const AllSearchPage = lazy(() => import("../../ui/pages/AllSearchPage/AllSearchPage"));

function useShouldRedirectToLogin() {
  const queryClient = useQueryClient();
  const isProfileReady = queryClient.getQueryCache().find<ProfileDto>({ queryKey: ["loginWhoAmI"] })?.state
    .data?.isProfileReady;
  const token = useSelector(({ auth: { token } }) => token);

  // Don't redirect if we're on the callback route
  if (window.location.pathname === "/login/google/callback") {
    return false;
  }

  return Boolean(!token || isTokenExpired(token) || (status === "success" && !isProfileReady));
}

function WithRedirectToLogin({ children }: React.PropsWithChildren) {
  const shouldRedirectToLogin = useShouldRedirectToLogin();
  if (shouldRedirectToLogin) {
    return <RedirectToLogin />;
  }

  return <>{children}</>;
}

export const routes: RouteObject[] = [
  // Base route; Redirect to login if not authenticated; Redirect to home if authenticated
  {
    element: (
      <WithRedirectToLogin>
        <Navigate to={Route.home()} />
      </WithRedirectToLogin>
    ),
    path: RawRoute.index,
  },

  // Routes not requiring authentication; No nav menu
  {
    children: [
      { element: <LoginAsync />, path: RawRoute.login },
      { element: <LoginCallbackAsync />, path: RawRoute.loginCallback },
      { element: <Logout />, path: RawRoute.logout },
      { element: <LoginVerificationAsync />, path: RawRoute.verify },
      { element: <CreateProfileAsync />, path: RawRoute.profileCreate },
    ],
    element: <LegacyAppNavigationLayoutAsync />,
  },

  // Routes requiring authentication; No nav menu
  {
    children: [
      {
        children: [
          { element: <QRCodePage />, path: RawRoute.podQr },
          { element: <QRCodePage />, path: RawRoute.eventQr },
          { element: <QRCodePage />, path: RawRoute.bubbleQr },
          { element: <PodCheckIn />, path: RawRoute.podCheckIn },
          { element: <EventCheckIn />, path: RawRoute.eventCheckIn },
          { element: <CheckInPage />, path: RawRoute.bubbleCheckIn },
        ],
        element: <LegacyAppNavigationLayoutAsync />,
      },
    ],
    element: (
      <WithRedirectToLogin>
        <Outlet />
      </WithRedirectToLogin>
    ),
  },

  // Routes requiring authentication; Nav menu
  {
    children: [
      // with app navigation layout
      {
        children: [
          // home
          { element: <HomeAsync />, path: RawRoute.home },

          // admin
          { element: <AdminPageAsync />, path: RawRoute.admin },
          // Site admin - redirect base route to Platform Insights
          { element: <Navigate replace to={Route.adminSiteDataAnalytics()} />, path: RawRoute.adminSite },
          // Site admin pages
          { element: <DataAnalyticsPage />, path: RawRoute.adminSiteDataAnalytics },
          { element: <EntityManagementPage />, path: RawRoute.adminSiteEntityManagement },

          // organizations
          {
            element: <AdminOrganizationsPage />,
            path: RawRoute.organizations,
          },
          {
            element: <MyOrganizationsPage />,
            path: RawRoute.myOrganizations,
          },
          {
            element: <OrganizationAsync />,
            path: RawRoute.organization,
          },

          // notifications
          { element: <InboxAsync />, path: RawRoute.notifications },
          { element: <ArchivedAsync />, path: RawRoute.notificationsArchived },

          // payments
          { element: <ProfileRefresh />, path: RawRoute.profileRefresh },
          { element: <PaymentSuccess />, path: RawRoute.paymentSuccess },
          { element: <PaymentCancel />, path: RawRoute.paymentCancel },

          // profile
          { element: <UserProfileAsync />, path: RawRoute.profile },

          // ai tutor
          { element: <AIChatPageAsync />, path: RawRoute.aiTutor },

          // tasks
          { element: <TaskDashboardPage />, path: RawRoute.taskDashboard },

          // rooms
          { element: <RoomDashboardPage />, path: RawRoute.roomView },
          {
            element: (
              <Suspense fallback={<LoadingIndicatorFullPage open />}>
                <MyRoomsPageAsync />
              </Suspense>
            ),
            path: RawRoute.myRooms,
          },
          {
            element: (
              <Suspense fallback={<LoadingIndicatorFullPage open />}>
                <Navigate replace to={`${Route.allSearch()}?type=rooms`} />
              </Suspense>
            ),
            path: RawRoute.roomSearch,
          },
          {
            element: (
              <Suspense fallback={<LoadingIndicatorFullPage open />}>
                <Navigate replace to={`${Route.allSearch()}?type=events`} />
              </Suspense>
            ),
            path: RawRoute.eventSearch,
          },
          {
            element: (
              <Suspense fallback={<LoadingIndicatorFullPage open />}>
                <AllSearchPage />
              </Suspense>
            ),
            path: RawRoute.allSearch,
          },

          // events
          { element: <EventDashboardPage />, path: RawRoute.eventView },
          {
            element: (
              <Suspense fallback={<LoadingIndicatorFullPage open />}>
                <MyEventsPageAsync />
              </Suspense>
            ),
            path: RawRoute.myEvents,
          },

          // resources
          {
            element: (
              <Suspense fallback={<LoadingIndicatorFullPage open />}>
                <ResourceLibraryPageAsync />
              </Suspense>
            ),
            path: RawRoute.resourceLibrary,
          },

          // resume
          { element: <ResumeBuilderAsync />, path: RawRoute.resumeBuilder },

          // sales
          { element: <SalesHomeAsync />, path: RawRoute.salesPortalHome },
          { element: <PipelineAsync />, path: RawRoute.salesPortalPipeline },
          { element: <ProspectsAsync />, path: RawRoute.salesPortalProspects },
          { element: <ProspectDetailsAsync />, path: RawRoute.salesPortalProspectDetails },

          // search
          { element: <CandidateSearchAsync />, path: RawRoute.searchCandidates },
        ],
        element: <AppLayoutAsync />,
      },
    ],
    element: (
      <WithRedirectToLogin>
        <Outlet />
      </WithRedirectToLogin>
    ),
  },
];

export const browserRouter = createBrowserRouter(routes, { future: { v7_relativeSplatPath: true } });

export const memoryRouter = createMemoryRouter(routes, { future: { v7_relativeSplatPath: true } });
